/**
 * First-admin setup wizard (rendered OUTSIDE the app shell).
 *
 * Shown on first run when no local admin account exists yet
 * (`setupState.needsSetup === true`). Creating the account promotes the
 * user to administrator and — because a local admin exists — locks the API
 * so subsequent access requires authentication.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api';
import { useAuth } from '../auth/AuthContext';

export function AuthSetupPage() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirm) {
      setError('passwords do not match');
      return;
    }
    setBusy(true);
    try {
      await api.authSetup(username, password);
      await refresh();
      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'setup failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth-screen ip-shell" data-testid="auth-setup">
      <form className="auth-card" onSubmit={submit}>
        <h1>Create the admin account</h1>
        <p className="auth-sub">
          This is the first account. It becomes the administrator and locks the
          API.
        </p>
        {error && (
          <div className="badge badge--err" role="alert">
            {error}
          </div>
        )}
        <label>
          Username
          <input
            data-testid="setup-username"
            name="username"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoFocus
          />
        </label>
        <label>
          Password
          <input
            data-testid="setup-password"
            name="new-password"
            autoComplete="new-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        <label>
          Confirm password
          <input
            data-testid="setup-confirm"
            name="confirm-password"
            autoComplete="new-password"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </label>
        <button data-testid="setup-submit" type="submit" disabled={busy}>
          {busy ? 'Creating…' : 'Create admin'}
        </button>
      </form>
    </div>
  );
}
